// src/components/FormattedText.js
// Lightweight markdown-to-HTML for admin-entered content.
// Supports: **bold**, *italic*, [links](url), - bullet lists, line breaks.
import React, { useRef, useState } from 'react';

function parseMarkdown(text) {
    if (!text) return '';
    let html = text
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>')
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color:var(--mom-link);text-decoration:underline">$1</a>');

    const lines = html.split('\n');
    const result = [];
    let inList = false;
    for (const line of lines) {
        const trimmed = line.trim();
        if (/^[-•]\s+/.test(trimmed)) {
            if (!inList) { result.push('<ul style="margin:8px 0;padding-left:20px">'); inList = true; }
            result.push('<li style="margin-bottom:4px">' + trimmed.replace(/^[-•]\s+/, '') + '</li>');
        } else {
            if (inList) { result.push('</ul>'); inList = false; }
            if (trimmed === '') { result.push('<br/>'); } else { result.push(line); }
        }
    }
    if (inList) result.push('</ul>');
    return result.join('\n');
}

function FormattedText({ text, style = {}, className = '' }) {
    if (!text) return null;
    const html = parseMarkdown(text);
    return (
        <div className={className} style={{ lineHeight: 1.8, ...style }}
            dangerouslySetInnerHTML={{ __html: html }} />
    );
}

// ── Rich text area with toolbar ──────────────────
export function RichTextArea({ value, onChange, placeholder, rows = 3 }) {
    const ref = useRef(null);
    const [showPreview, setShowPreview] = useState(false);
    const [linkOpen, setLinkOpen] = useState(false);
    const [linkText, setLinkText] = useState('');
    const [linkUrl, setLinkUrl] = useState('');
    const selRef = useRef({ start: 0, end: 0 });
    const [focused, setFocused] = useState(false);
    const [hovered, setHovered] = useState(null);

    const wrapSelection = (before, after, fallback) => {
        const ta = ref.current;
        if (!ta) return;
        ta.focus();
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        const text = value || '';
        const selected = text.substring(start, end);
        if (selected) {
            const newText = text.substring(0, start) + before + selected + after + text.substring(end);
            onChange(newText);
            setTimeout(() => { ta.selectionStart = start + before.length; ta.selectionEnd = end + before.length; }, 0);
        } else {
            const insert = before + fallback + after;
            const newText = text.substring(0, start) + insert + text.substring(end);
            onChange(newText);
            setTimeout(() => { ta.selectionStart = start + before.length; ta.selectionEnd = start + before.length + fallback.length; }, 0);
        }
    };

    // Inline link popover — replaces window.prompt() for a premium, on-brand feel.
    const openLinkPopover = () => {
        const ta = ref.current;
        const text = value || '';
        let start = text.length;
        let end = text.length;
        if (ta) { start = ta.selectionStart; end = ta.selectionEnd; }
        selRef.current = { start, end };
        setLinkText(text.substring(start, end));
        setLinkUrl('');
        setLinkOpen(true);
    };
    const applyLink = () => {
        const { start, end } = selRef.current;
        const text = value || '';
        const label = (linkText || 'link').trim();
        let url = (linkUrl || '').trim();
        if (url && !/^https?:\/\//i.test(url) && !url.startsWith('/') && !url.startsWith('mailto:')) url = 'https://' + url;
        const md = '[' + label + '](' + (url || 'https://') + ')';
        onChange(text.substring(0, start) + md + text.substring(end));
        setLinkOpen(false);
        setTimeout(() => {
            const ta = ref.current;
            if (ta) { ta.focus(); const pos = start + md.length; ta.selectionStart = ta.selectionEnd = pos; }
        }, 0);
    };

    const insertBullet = () => {
        const ta = ref.current;
        if (!ta) return;
        ta.focus();
        const start = ta.selectionStart;
        const text = value || '';
        const beforeCursor = text.substring(0, start);
        const needsNewline = beforeCursor.length > 0 && !beforeCursor.endsWith('\n');
        const prefix = (needsNewline ? '\n' : '') + '- ';
        const newText = text.substring(0, start) + prefix + text.substring(start);
        onChange(newText);
        setTimeout(() => { ta.selectionStart = ta.selectionEnd = start + prefix.length; }, 0);
    };

    // Slim style retained only for the link-popover buttons below.
    const tb = {
        padding: '6px 12px', borderRadius: 8, border: '1px solid var(--mom-cream-dark)',
        background: 'var(--mom-white)', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)',
        fontSize: 12, color: 'var(--mom-charcoal)', lineHeight: 1, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
    };
    // Modern minimal toolbar — borderless icon buttons in a floating pill, revealed
    // only while the editor is focused so the field reads as calm text at rest.
    const iconBtn = (key) => ({
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 30, height: 30, borderRadius: 8, border: 'none', padding: 0,
        background: hovered === key ? 'var(--mom-cream-dark)' : 'transparent',
        color: 'var(--mom-charcoal)', cursor: 'pointer', transition: 'background 0.12s',
    });
    const noBlur = (e) => e.preventDefault(); // keep the textarea focused when a tool is clicked

    return (
        <div style={{ position: 'relative' }}>
            {(focused || linkOpen) && (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 2, marginBottom: 8, padding: 4, background: 'var(--mom-white)', borderRadius: 10, border: '1px solid var(--mom-cream-dark)', boxShadow: '0 6px 18px rgba(0,0,0,0.07)', animation: 'fadeIn 0.15s ease' }}>
                    <button type="button" style={iconBtn('bold')} onMouseDown={noBlur} onMouseEnter={() => setHovered('bold')} onMouseLeave={() => setHovered(null)} onClick={() => wrapSelection('**', '**', 'bold text')} title="Bold"><strong style={{ fontSize: 14 }}>B</strong></button>
                    <button type="button" style={iconBtn('italic')} onMouseDown={noBlur} onMouseEnter={() => setHovered('italic')} onMouseLeave={() => setHovered(null)} onClick={() => wrapSelection('*', '*', 'italic text')} title="Italic"><em style={{ fontFamily: 'Georgia, serif', fontSize: 14 }}>I</em></button>
                    <button type="button" style={iconBtn('link')} onMouseDown={noBlur} onMouseEnter={() => setHovered('link')} onMouseLeave={() => setHovered(null)} onClick={openLinkPopover} title="Insert link">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
                    </button>
                    <button type="button" style={iconBtn('bullet')} onMouseDown={noBlur} onMouseEnter={() => setHovered('bullet')} onMouseLeave={() => setHovered(null)} onClick={insertBullet} title="Bullet list">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="9" y1="6" x2="20" y2="6" /><line x1="9" y1="12" x2="20" y2="12" /><line x1="9" y1="18" x2="20" y2="18" /><circle cx="4" cy="6" r="1.5" fill="currentColor" stroke="none" /><circle cx="4" cy="12" r="1.5" fill="currentColor" stroke="none" /><circle cx="4" cy="18" r="1.5" fill="currentColor" stroke="none" /></svg>
                    </button>
                    <div style={{ width: 1, height: 18, background: 'var(--mom-cream-dark)', margin: '0 4px' }} />
                    <button type="button" onMouseDown={noBlur} onMouseEnter={() => setHovered('preview')} onMouseLeave={() => setHovered(null)} onClick={() => setShowPreview(!showPreview)} title="Toggle preview"
                        style={{ display: 'flex', alignItems: 'center', height: 30, padding: '0 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 600, letterSpacing: '0.02em', background: showPreview ? 'var(--mom-charcoal)' : (hovered === 'preview' ? 'var(--mom-cream-dark)' : 'transparent'), color: showPreview ? 'var(--mom-white)' : 'var(--mom-charcoal)', transition: 'background 0.12s' }}>
                        {showPreview ? 'Hide preview' : 'Preview'}
                    </button>
                </div>
            )}

            {/* Inline link popover */}
            {linkOpen && (
                <div style={{ position: 'absolute', top: 46, left: 0, zIndex: 30, width: 'min(300px, 92%)', background: 'var(--mom-white)', border: '1px solid var(--mom-cream-dark)', borderRadius: 12, boxShadow: '0 12px 32px rgba(0,0,0,0.16)', padding: 14 }}>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 700, color: 'var(--mom-charcoal)', marginBottom: 8 }}>Insert link</p>
                    <input className="mom-input" value={linkText} onChange={(e) => setLinkText(e.target.value)} placeholder="Link text" style={{ fontSize: 13, marginBottom: 8 }} />
                    <input className="mom-input" autoFocus value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyLink(); } if (e.key === 'Escape') setLinkOpen(false); }}
                        placeholder="https://…" style={{ fontSize: 13 }} />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                        <button type="button" onClick={() => setLinkOpen(false)} style={{ ...tb, minWidth: 0, padding: '6px 12px', fontSize: 12 }}>Cancel</button>
                        <button type="button" onClick={applyLink} style={{ ...tb, minWidth: 0, padding: '6px 14px', fontSize: 12, background: 'var(--mom-charcoal)', color: 'var(--mom-white)', borderColor: 'var(--mom-charcoal)' }}>Insert link</button>
                    </div>
                </div>
            )}

            {/* Editor — always visible (document feel) */}
            <textarea ref={ref} className="mom-input" value={value || ''} onChange={(e) => onChange(e.target.value)}
                onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
                placeholder={placeholder} rows={rows} style={{ resize: 'vertical', lineHeight: 1.6 }} />

            {/* Live preview — updates as you type; the editor stays in place above it */}
            {showPreview && (
                <div style={{ marginTop: 8, padding: '14px 16px', background: 'var(--mom-cream)', border: '1px solid var(--mom-cream-dark)', borderRadius: 12 }}>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--mom-warm-gray)', marginBottom: 8 }}>Live preview</p>
                    {(value || '').trim() ? (
                        <FormattedText text={value} style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-charcoal)' }} />
                    ) : (
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-warm-gray)', fontStyle: 'italic' }}>Nothing to preview yet…</p>
                    )}
                </div>
            )}
        </div>
    );
}

// Keep for backwards compat — now a no-op since toolbar replaces it
export function FormattingHint() { return null; }

export default FormattedText;